import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  VStack,
  useColorModeValue,
  useToast,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  InputGroup,
  InputLeftElement,
  Select,
  HStack,
  Tag,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  Grid,
  GridItem,
  Spinner,
  Icon,
  Tooltip,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PublicNavbar } from "../layout/PublicNavbar";
import { Footer } from "../layout/Footer";
import { paymentsAPI, aiAPI, userAPI, systemAPI } from "../../services";
import { PrivateNavbar } from "components/layout/PrivateNavbar";
import {
  FiDollarSign,
  FiCopy,
  FiCheck,
  FiCreditCard,
  FiLock,
} from "react-icons/fi";
import EfiPay from "payment-token-efi";

interface CreditCardForm {
  name: string;
  cpf: string;
  email: string;
  phone: string;
  birth: string;
  street: string;
  number: string;
  neighborhood: string;
  zipcode: string;
  city: string;
  state: string;
  complement?: string;
  card_number: string;
  card_cvv: string;
  card_expiration_month: string;
  card_expiration_year: string;
  card_brand: string;
}

export const AddFunds: React.FC = () => {
  const [amount, setAmount] = useState<number>(10);

  const [bonus, setBonus] = useState<number>(2.16 / 7.2);
  const [bonusPercentage, setBonusPercentage] = useState<number>(3);
  const [isLoading, setIsLoading] = useState(false);
  const [currencyPairs, setCurrencyPairs] = useState<any>({ usd_brl: 5 });
  const [paymentGateway, setPaymentGateway] = useState("efipay-pix");
  const [cardMask, setCardMask] = useState("");
  const [pixCode, setPixCode] = useState("");
  const [pixTxid, setPixTxid] = useState("");
  const [pixStatus, setPixStatus] = useState<"PENDING" | "CONCLUIDA">(
    "PENDING"
  );
  const [userCpf, setUserCpf] = useState("");
  const [isCpfValid, setIsCpfValid] = useState(true);
  const pixPollRef = useRef<NodeJS.Timeout>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isPixOpen,
    onOpen: onPixOpen,
    onClose: onPixClose,
  } = useDisclosure();

  const [depositBonus, setDepositBonus] = useState<any>({});

  const [creditCardForm, setCreditCardForm] = useState<CreditCardForm>({
    name: "",
    cpf: "",
    email: "",
    phone: "",
    birth: "",
    street: "",
    number: "",
    neighborhood: "",
    zipcode: "",
    city: "",
    state: "",
    complement: "",
    card_number: "",
    card_cvv: "",
    card_expiration_month: "",
    card_expiration_year: "",
    card_brand: "",
  });
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const showBrlPrice = i18n.language === "pt-BR";

  useEffect(() => {
    const loadCurrencyPairs = async () => {
      try {
        const pairs = await systemAPI.getCurrencyPairs();
        setCurrencyPairs(pairs);
      } catch (error) {
        console.error("Error loading currency pairs:", error);
      }
    };
    loadCurrencyPairs();
    loadDepositBonus();
  }, []);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const userData = await userAPI.getCurrentUser();
        if (userData.cpf) {
          setUserCpf(userData.cpf);
        }
      } catch (error) {
        console.error("Error loading user data:", error);
      }
    };
    loadUserData();
  }, []);

  const handleAmountChange = (valueAsString: string, valueAsNumber: number) => {
    setAmount(valueAsNumber);
    // if bonus > 0, show bonus
    if (valueAsNumber > 0) {
      setBonus(depositBonus["0"] * valueAsNumber);
      setBonusPercentage(depositBonus["0"] * 100);
    }
    if (valueAsNumber > 20) {
      setBonus(depositBonus["20"] * valueAsNumber);
      setBonusPercentage(depositBonus["20"] * 100);
    }
    if (valueAsNumber > 50) {
      setBonus(depositBonus["50"] * valueAsNumber);
      setBonusPercentage(depositBonus["50"] * 100);
    }
    if (valueAsNumber > 100) {
      setBonus(depositBonus["100"] * valueAsNumber);
      setBonusPercentage(depositBonus["100"] * 100);
    }
  };

  const getBrlPrice = (value: number) => {
    return value * currencyPairs.usd_brl;
  };

  const loadDepositBonus = async () => {
    const depositBonus = await systemAPI.getDepositBonuses();
    setDepositBonus(depositBonus);
  };

  const handleCreditCardSubmit = async () => {
    setIsLoading(true);
    try {
      // First identify the card brand
      const brand = await EfiPay.CreditCard.setAccount(
        "a2b054f1eb5a81c7a38c98cb0ec7a03f"
      )
        .setEnvironment("sandbox")
        .setCardNumber(creditCardForm.card_number.replace(/\D/g, ""))
        .verifyCardBrand();
      // Then get the payment token
      const response = await EfiPay.CreditCard.setAccount(
        "a2b054f1eb5a81c7a38c98cb0ec7a03f"
      )
        .setEnvironment("sandbox")
        .setBrand(brand)
        .setCreditCardData({
          brand,
          number: creditCardForm.card_number.replace(/\D/g, ""),
          cvv: creditCardForm.card_cvv,
          expirationMonth: creditCardForm.card_expiration_month,
          expirationYear: creditCardForm.card_expiration_year,
          holderName: creditCardForm.name,
          holderDocument: creditCardForm.cpf.replace(/\D/g, ""),
          reuse: false,
        })
        .getPaymentToken();

      if ("error" in response) {
        throw new Error(response.error_description);
      }

      setCardMask(response.card_mask);

      const paymentAmount = getBrlPrice(amount);
      const payment = await paymentsAPI.createPrepaidPayment(
        paymentAmount,
        "Adição de créditos",
        paymentGateway,
        (response as EfiPay.CreditCard.PaymentTokenResponse).payment_token
      );
      window.location.href = payment.paymentUrl;
    } catch (error) {
      console.error("Error processing credit card:", error);
      toast({
        title: t("payments.addFunds.error"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (pixPollRef.current) {
        clearInterval(pixPollRef.current);
      }
    };
  }, []);

  const startPixPolling = (txid: string) => {
    // Clear any existing interval
    if (pixPollRef.current) {
      clearInterval(pixPollRef.current);
    }

    const startTime = Date.now();
    const pollInterval = 6000; // 6 seconds
    const maxDuration = 3600000; // 1 hour in milliseconds

    const pollPixStatus = async () => {
      try {
        const details = await paymentsAPI.getPixDetails(txid);
        if (details.status === "CONCLUIDA") {
          setPixStatus("CONCLUIDA");
          clearInterval(pixPollRef.current);
          onPixClose();
          // window.location.reload();
          navigate("/payments");
        }

        // Check if we've exceeded the max duration
        if (Date.now() - startTime >= maxDuration) {
          clearInterval(pixPollRef.current);
          onPixClose();
          toast({
            title: "O código Pix expirou",
            description: "Por favor, gere um novo código",
            status: "error",
            duration: 5000,
          });
        }
      } catch (error) {
        console.error("Error polling Pix status:", error);
      }
    };

    // Start polling
    pixPollRef.current = setInterval(pollPixStatus, pollInterval);
  };

  const validateCpf = (cpf: string) => {
    const cpfRegex = /^\d{3}\.?\d{3}\.?\d{3}-?\d{2}$/;
    return cpfRegex.test(cpf);
  };

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUserCpf(value);
    setIsCpfValid(validateCpf(value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (amount === 0) {
      toast({
        title: "Amount is required",
        description: "Please enter an amount to proceed with the payment",
        status: "error",
        duration: 5000,
      });
      return;
    }

    if (
      (paymentGateway === "efipay-pix" || paymentGateway === "efipay-card") &&
      !userCpf
    ) {
      toast({
        title: "CPF Required",
        description: "Please enter your CPF to proceed with the payment",
        status: "error",
        duration: 5000,
      });
      return;
    }

    if (
      (paymentGateway === "efipay-pix" || paymentGateway === "efipay-card") &&
      !isCpfValid
    ) {
      toast({
        title: "Invalid CPF",
        description: "Please enter a valid CPF",
        status: "error",
        duration: 5000,
      });
      return;
    }

    if (paymentGateway === "efipay-card") {
      onOpen();
      return;
    }

    setIsLoading(true);
    try {
      // Update user CPF if needed
      if (
        (paymentGateway === "efipay-pix" || paymentGateway === "efipay-card") &&
        userCpf
      ) {
        await userAPI.updateProfile({ cpf: userCpf });
      }

      const paymentAmount = paymentGateway.includes("efipay")
        ? getBrlPrice(amount)
        : amount;
      const payment = await paymentsAPI.createPrepaidPayment(
        paymentAmount,
        "Adição de créditos",
        paymentGateway
      );

      if (paymentGateway === "efipay-pix" && payment.pixCopiaECola) {
        setPixCode(payment.pixCopiaECola);
        setPixTxid(payment.chargeId);
        setPixStatus("PENDING");
        onPixOpen();
        startPixPolling(payment.chargeId);
      } else {
        window.location.href = payment.paymentUrl;
      }
    } catch (error) {
      console.error("Error creating payment:", error);
      toast({
        title: t("payments.addFunds.error"),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCreditCardForm((prev) => ({
      ...prev,
      [name]: value,
    }));
    // update the card brand
    const brand = await EfiPay.CreditCard.setAccount(
      "a2b054f1eb5a81c7a38c98cb0ec7a03f"
    )
      .setEnvironment("sandbox")
      .setCardNumber(creditCardForm.card_number.replace(/\D/g, ""))
      .verifyCardBrand();
    setCreditCardForm((prev) => ({
      ...prev,
      card_brand: brand,
    }));
  };

  const handleCopyPix = () => {
    navigator.clipboard.writeText(pixCode);
    toast({
      title: "Código Pix copiado!",
      status: "success",
      duration: 2000,
    });
  };

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <PrivateNavbar />

      <Box flex="1" bg={bgColor} py={20}>
        <Container maxW="container.sm">
          <VStack spacing={8} align="stretch">
            <VStack spacing={4} textAlign="center">
              <Heading
                size="xl"
                fontFamily="Nasalization"
                bgGradient="linear(to-r, brand.primary, brand.secondary)"
                bgClip="text"
              >
                {t("payments.addFunds.title")}
              </Heading>
              <Text
                fontSize="lg"
                color={useColorModeValue("gray.600", "gray.400")}
              >
                {t("payments.addFunds.subtitle")}
              </Text>
            </VStack>

            <Box
              as="form"
              onSubmit={handleSubmit}
              bg={useColorModeValue("white", "gray.800")}
              p={8}
              borderRadius="xl"
              shadow="base"
            >
              <VStack spacing={6}>
                <FormControl isRequired>
                  <HStack justifyContent="space-between">
                    <FormLabel>{t("payments.addFunds.amount")} (U$)</FormLabel>
                    <Tag fontWeight="bold" fontSize="xs" colorScheme="blue">
                      Dólar: R${" "}
                      {currencyPairs.usd_brl.toFixed(2).replace(".", ",")}
                    </Tag>
                  </HStack>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <FiDollarSign />
                    </InputLeftElement>
                    <NumberInput
                      min={process.env.NODE_ENV === "development" ? 1 : 5}
                      max={500}
                      value={amount}
                      onChange={handleAmountChange}
                      width="full"
                    >
                      <NumberInputField pl={8} />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </InputGroup>
                  {showBrlPrice && (
                    <HStack>
                      <Tag
                        mt={2}
                        fontWeight="bold"
                        fontSize="xs"
                        colorScheme="blue"
                      >
                        A Pagar: R${" "}
                        {getBrlPrice(amount).toFixed(2).replace(".", ",")}
                      </Tag>
                      {bonus > 0 && (
                        <Tooltip label={t("payments.addFunds.bonus")}>
                          <Tag
                            mt={2}
                            fontWeight="bold"
                            fontSize="xs"
                            colorScheme="green"
                          >
                            {`Bônus: ${bonusPercentage.toFixed(
                              0
                            )}% = R$ ${getBrlPrice(bonus)
                              .toFixed(2)
                              .replace(".", ",")}`}{" "}
                          </Tag>
                        </Tooltip>
                      )}

                      <Tooltip
                        label={t("payments.addFunds.total")}
                        minWidth="330px"
                      >
                        <Tag
                          mt={2}
                          fontWeight="bold"
                          fontSize="xs"
                          color="brand.primary"
                        >
                          A Receber: R${" "}
                          {getBrlPrice(amount + bonus)
                            .toFixed(3)
                            .replace(".", ",")}
                        </Tag>
                      </Tooltip>
                    </HStack>
                  )}
                  {/* // show bonus */}
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>{t("payments.addFunds.gateway")}</FormLabel>
                  <Select
                    value={paymentGateway}
                    onChange={(e) => setPaymentGateway(e.target.value)}
                  >
                    <option value="efipay-pix">PIX</option>
                    <option value="efipay-card" disabled>
                      Cartão de Crédito
                    </option>
                  </Select>
                </FormControl>
                {(paymentGateway === "efipay-pix" ||
                  paymentGateway === "efipay-card") && (
                  <FormControl isRequired isInvalid={!isCpfValid}>
                    <FormLabel>CPF</FormLabel>
                    <Input
                      value={userCpf}
                      onChange={handleCpfChange}
                      placeholder="000.000.000-00"
                    />
                    {!isCpfValid && (
                      <Text color="red.500" fontSize="sm" mt={1}>
                        Please enter a valid CPF
                      </Text>
                    )}
                  </FormControl>
                )}
                <Button
                  type="submit"
                  colorScheme="brand"
                  size="lg"
                  width="full"
                  isLoading={isLoading}
                  loadingText={t("payments.addFunds.processing")}
                >
                  {t("payments.addFunds.submit")}
                </Button>
                <Text
                  fontSize="sm"
                  color="gray.500"
                  textAlign="center"
                  fontStyle="italic"
                >
                  <Icon as={FiLock} mr={2} /> {t("payments.addFunds.note")}
                </Text>
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>

      <Footer />

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Dados do Cartão</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Grid templateColumns="repeat(2, 1fr)" gap={4} width="100%">
                <GridItem colSpan={2}>
                  <FormControl isRequired>
                    <FormLabel>Nome Completo</FormLabel>
                    <Input
                      name="name"
                      value={creditCardForm.name}
                      onChange={handleInputChange}
                    />
                  </FormControl>
                </GridItem>
                <FormControl isRequired>
                  <FormLabel>CPF</FormLabel>
                  <Input
                    name="cpf"
                    value={creditCardForm.cpf}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Email</FormLabel>
                  <Input
                    name="email"
                    type="email"
                    value={creditCardForm.email}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Telefone</FormLabel>
                  <Input
                    name="phone"
                    value={creditCardForm.phone}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Data de Nascimento</FormLabel>
                  <Input
                    name="birth"
                    type="date"
                    value={creditCardForm.birth}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <GridItem colSpan={2}>
                  <FormControl isRequired>
                    <FormLabel>Rua</FormLabel>
                    <Input
                      name="street"
                      value={creditCardForm.street}
                      onChange={handleInputChange}
                    />
                  </FormControl>
                </GridItem>
                <FormControl isRequired>
                  <FormLabel>Número</FormLabel>
                  <Input
                    name="number"
                    value={creditCardForm.number}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Complemento</FormLabel>
                  <Input
                    name="complement"
                    value={creditCardForm.complement}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Bairro</FormLabel>
                  <Input
                    name="neighborhood"
                    value={creditCardForm.neighborhood}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>CEP</FormLabel>
                  <Input
                    name="zipcode"
                    value={creditCardForm.zipcode}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Cidade</FormLabel>
                  <Input
                    name="city"
                    value={creditCardForm.city}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Estado</FormLabel>
                  <Input
                    name="state"
                    value={creditCardForm.state}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <GridItem colSpan={2}>
                  <Heading size="md" my={4}>
                    Dados do Cartão
                  </Heading>
                </GridItem>
                <GridItem colSpan={2}>
                  <FormControl isRequired>
                    <FormLabel>Número do Cartão</FormLabel>
                    <Input
                      name="card_number"
                      value={creditCardForm.card_number}
                      onChange={handleInputChange}
                    />
                  </FormControl>
                </GridItem>
                <FormControl isRequired>
                  <FormLabel>CVV</FormLabel>
                  <Input
                    name="card_cvv"
                    maxLength={4}
                    value={creditCardForm.card_cvv}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Bandeira</FormLabel>
                  <Input
                    name="card_brand"
                    value={creditCardForm.card_brand}
                    // onChange={handleInputChange}
                    disabled
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Mês de Expiração</FormLabel>
                  <Input
                    name="card_expiration_month"
                    maxLength={2}
                    value={creditCardForm.card_expiration_month}
                    onChange={handleInputChange}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Ano de Expiração</FormLabel>
                  <Input
                    name="card_expiration_year"
                    maxLength={4}
                    value={creditCardForm.card_expiration_year}
                    onChange={handleInputChange}
                  />
                </FormControl>
              </Grid>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="gray" mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="brand"
              onClick={handleCreditCardSubmit}
              isLoading={isLoading}
            >
              Pagar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isPixOpen}
        onClose={() => {
          onPixClose();
          if (pixPollRef.current) {
            clearInterval(pixPollRef.current);
          }
        }}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Pagamento via Pix</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Box display="flex" justifyContent="center" width="100%" py={4}>
                {pixStatus === "PENDING" ? (
                  <Spinner
                    thickness="4px"
                    speed="0.65s"
                    emptyColor="gray.200"
                    color="brand.primary"
                    size="xl"
                  />
                ) : (
                  <Icon as={FiCheck} w={8} h={8} color="green.500" />
                )}
              </Box>
              <Text>Copie o código Pix abaixo:</Text>
              <Box
                p={4}
                bg={useColorModeValue("gray.100", "gray.700")}
                borderRadius="md"
                width="100%"
                wordBreak="break-all"
              >
                {pixCode}
              </Box>
              <Button
                leftIcon={<FiCopy />}
                colorScheme="brand"
                onClick={handleCopyPix}
                width="100%"
              >
                Copiar código Pix
              </Button>
              <Text fontSize="sm" color="gray.500" textAlign="center">
                Este código Pix expira em 1 hora, os créditos serão liberados
                imediatamente após a aprovação.
              </Text>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};
